import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

const WEDDING_DATE = new Date("2026-10-07T00:00:00");

function getTimeLeft() {
  const now = new Date();
  const diff = WEDDING_DATE - now;
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60)
  };
}

const CountdownSection = () => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(getTimeLeft);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, []);

  const units = [
    { key: "days", label: t("days"), value: timeLeft.days },
    { key: "hours", label: t("hours"), value: timeLeft.hours },
    { key: "minutes", label: t("minutes"), value: timeLeft.minutes },
    { key: "seconds", label: t("seconds"), value: timeLeft.seconds }
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
  };

  return (
    <section data-testid="countdown-section" className="bg-charcoal py-24 md:py-32 px-6 relative overflow-hidden">
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIvPjwvc3ZnPg==')]" />

      <motion.div
        className="max-w-4xl mx-auto text-center relative z-10"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <motion.p
          variants={fadeIn}
          className="font-serif text-gold text-xl md:text-2xl italic mb-4"
        >
          {t("save_the_date")}
        </motion.p>
        <motion.p
          variants={fadeIn}
          className="font-sans text-white/50 text-xs tracking-[0.3em] uppercase mb-16"
        >
          {t("countdown_label")}
        </motion.p>

        <div className="grid grid-cols-4 gap-4 md:gap-8">
          {units.map((unit, i) => (
            <motion.div
              key={unit.key}
              variants={fadeIn}
              custom={i}
              className="flex flex-col items-center"
            >
              <div className="relative">
                <span
                  data-testid={`countdown-${unit.key}`}
                  className="font-serif text-white text-4xl md:text-6xl lg:text-7xl tabular-nums block"
                >
                  {String(unit.value).padStart(2, "0")}
                </span>
                <div className="w-8 h-px bg-gold/40 mx-auto mt-4 mb-3" />
              </div>
              <span className="font-sans text-white/40 text-xs md:text-sm tracking-[0.2em] uppercase">
                {unit.label}
              </span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default CountdownSection;
